import type { FastifyInstance } from 'fastify'
import type { GamificationAdminController } from './gamification-admin.controller'
import { authMiddleware } from '../../middleware/auth.middleware'
import { adminMiddleware } from './admin.middleware'

/**
 * Gamification Admin Routes - Admin-only endpoints
 * 
 * All routes require:
 * 1. Authentication (authMiddleware)
 * 2. Admin role (adminMiddleware)
 */
export async function gamificationAdminRoutes(
  app: FastifyInstance,
  controller: GamificationAdminController
) {
  // Apply auth + admin check to all routes
  app.addHook('onRequest', authMiddleware)
  app.addHook('onRequest', adminMiddleware)

  // ========== QUEST TEMPLATE ROUTES ==========

  app.post('/quest-templates', {
    handler: controller.createQuestTemplate.bind(controller)
  })

  app.get('/quest-templates', {
    handler: controller.getQuestTemplates.bind(controller)
  })

  app.get('/quest-templates/:templateId', {
    handler: controller.getQuestTemplateById.bind(controller)
  })

  app.patch('/quest-templates/:templateId', {
    handler: controller.updateQuestTemplate.bind(controller)
  })

  app.delete('/quest-templates/:templateId', {
    handler: controller.deleteQuestTemplate.bind(controller)
  })

  // ========== CHALLENGE TEMPLATE ROUTES ==========

  app.post('/challenge-templates', {
    handler: controller.createChallengeTemplate.bind(controller)
  })

  app.get('/challenge-templates', {
    handler: controller.getChallengeTemplates.bind(controller)
  })

  app.get('/challenge-templates/:templateId', {
    handler: controller.getChallengeTemplateById.bind(controller)
  })

  app.patch('/challenge-templates/:templateId', {
    handler: controller.updateChallengeTemplate.bind(controller)
  })

  app.delete('/challenge-templates/:templateId', {
    handler: controller.deleteChallengeTemplate.bind(controller)
  })

  // ========== SEASON CONFIG ROUTES ==========

  app.post('/seasons', {
    handler: controller.createSeasonConfig.bind(controller)
  })

  app.get('/seasons', {
    handler: controller.getSeasonConfigs.bind(controller)
  })

  app.get('/seasons/active', {
    handler: controller.getActiveSeasonConfig.bind(controller)
  })

  app.patch('/seasons/:configId', {
    handler: controller.updateSeasonConfig.bind(controller)
  })

  app.delete('/seasons/:configId', {
    handler: controller.deleteSeasonConfig.bind(controller)
  })

  // ========== BADGE ROUTES ==========

  app.post('/badges/preview', {
    handler: controller.generateBadgeConcept.bind(controller)
  })

  app.post('/badges', {
    handler: controller.createBadge.bind(controller)
  })

  app.get('/badges', {
    handler: controller.getAllBadges.bind(controller)
  })

  app.patch('/badges/:badgeId', {
    handler: controller.updateBadge.bind(controller)
  })

  app.delete('/badges/:badgeId', {
    handler: controller.deleteBadge.bind(controller)
  })

  app.post('/badges/assign', {
    handler: controller.assignBadgeToUser.bind(controller)
  })

  app.post('/badges/remove', {
    handler: controller.removeBadgeFromUser.bind(controller)
  })

  // ========== BULK OPERATIONS ==========

  app.post('/quest-templates/import', {
    handler: controller.importQuestTemplates.bind(controller)
  })

  app.post('/challenge-templates/import', {
    handler: controller.importChallengeTemplates.bind(controller)
  })

  app.get('/export', {
    handler: controller.exportAllTemplates.bind(controller)
  })

  // ========== USER QUEST/CHALLENGE MANAGEMENT ==========

  app.post('/user-quests/:questId/complete', {
    handler: controller.forceCompleteQuest.bind(controller)
  })

  app.delete('/user-quests/:questId', {
    handler: controller.deleteUserQuest.bind(controller)
  })

  app.post('/user-challenges/:challengeId/complete', {
    handler: controller.forceCompleteChallenge.bind(controller)
  })

  app.delete('/user-challenges/:challengeId', {
    handler: controller.deleteUserChallenge.bind(controller)
  })

  // ========== STATS & ANALYTICS ==========

  app.get('/stats/quests', {
    handler: controller.getQuestStats.bind(controller)
  })

  app.get('/stats/challenges', {
    handler: controller.getChallengeStats.bind(controller)
  })
}
